import { DatePicker, Table } from "antd";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import {
  FaArrowRight,
  FaBoxes,
  FaCalendarAlt,
  FaEye,
  FaInbox,
  FaPaperPlane,
  FaUserCheck,
  FaUserTimes,
} from "react-icons/fa";
import { MdPending } from "react-icons/md";
import { useHistory } from "react-router-dom";
import { API } from "../../Config";
import "./styles.css";
import { useSelector } from "react-redux";

// getStatusStyle and StatCard
const getStatusStyle = (status) => {
  const statusLower = status?.toLowerCase() || "";
  return (
    {
      paid: "statusPaid",
      completed: "statusCompleted",
      arrived: "statusArrived",
      departed: "statusDeparted",
      pending: "statusPending",
      scheduled: "statusScheduled",
      in_transit: "statusInTransit",
      loading: "statusLoading",
      end: "statusEnd",
      overdue: "statusOverdue",
      assigned: "statusAssigned",
      unassigned: "statusUnassigned",
    }[statusLower] || ""
  );
};

const StatCard = ({ title, value, icon: Icon, color, bgColor, onClick }) => (
  <div className="statCard" onClick={onClick}>
    <div className="statCardContent">
      <div className="statInfo">
        <p className="statTitle">{title}</p>
        <p className={`statValue ${color}`}>{value}</p>
      </div>
      <div className={`statIcon ${bgColor}`}>
        <Icon style={{ width: "16px", height: "16px", color: "white" }} />
      </div>
    </div>
  </div>
);

const DateFilter = ({
  dateFilter,
  setDateFilter,
  fetchOperationData,
  loading,
}) => (
  <div className="dateFilterContainer">
    <div className="dateFilter">
      <FaCalendarAlt className="dateFilterIcon" />
      <label className="dateFilterLabel">Select Date Range:</label>
      <DatePicker.RangePicker
        value={[moment(dateFilter.startDate), moment(dateFilter.endDate)]}
        onChange={(dates) => {
          if (dates) {
            setDateFilter({
              startDate: dates[0].format("YYYY-MM-DD"),
              endDate: dates[1].format("YYYY-MM-DD"),
            });
          }
        }}
        style={{ width: 300 }}
        className="dateRangePicker"
      />
    </div>
  </div>
);

const SectionHeader = ({ title, onSeeMore, showSeeMore = true }) => (
  <div className="sectionHeader">
    <h3 className="sectionTitle">{title}</h3>
    {showSeeMore && (
      <button className="seeMoreButton" onClick={onSeeMore}>
        <FaEye className="seeMoreIcon" />
        <span>See More</span>
        <FaArrowRight className="arrowIcon" />
      </button>
    )}
  </div>
);

const TabNavigation = ({ tabs, activeTab, onTabChange, color = "#3b82f6" }) => (
  <div className="tabNavigation">
    {tabs.map((tab) => (
      <button
        key={tab.key}
        onClick={() => onTabChange(tab.key)}
        className={`tabButton ${
          activeTab === tab.key ? "tabButtonActive" : "tabButtonInactive"
        }`}
        style={{
          ...(activeTab === tab.key
            ? {
                backgroundColor: color,
                color: "#ffffff",
                fontWeight: "600",
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px ${color}40`,
              }
            : {
                backgroundColor: "transparent",
                color: "#6b7280",
                fontWeight: "500",
              }),
        }}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

const OperationsDashboard = () => {
  const history = useHistory();
  const [operationData, setOperationData] = useState({
    pendingTripDetails: [],
    pendingTripCount: 0,
    inboundTripDetails: [],
    inboundTripCount: 0,
    outboundTripDetails: [],
    outboundTripCount: 0,
  });

  const [pickupData, setPickupData] = useState({
    assignedPickupDetails: [],
    assignedPickupCount: 0,
    unassignedPickupDetails: [],
    unassignedPickupCount: 0,
    totalPickupCount: 0,
  });

  // New state for pickup collection data
  const [pickupCollectionData, setPickupCollectionData] = useState({
    totalPickupCount: 0,
    totalAssignedPickupCount: 0,
    totalAssignedPickupDatas: [],
    totalUnassignedPickupCount: 0,
    totalUnassignedPickupDatas: [],
  });

  const [tripTypeTab, setTripTypeTab] = useState("inbound");
  const [pickupTypeTab, setPickupTypeTab] = useState("assigned");
  const [pickupCollectionTypeTab, setPickupCollectionTypeTab] =
    useState("assigned");
  const [dateFilter, setDateFilter] = useState({
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });
  const [loading, setLoading] = useState(false);
  const [pickupLoading, setPickupLoading] = useState(false);
  const [pickupCollectionLoading, setPickupCollectionLoading] = useState(false);
  const isMounted = useRef(true);
  const abortControllerRef = useRef(null);
  const { user } = useSelector((state) => state.Auth);

  const fetchOperationData = async () => {
    abortControllerRef.current = new AbortController();
    setLoading(true);
    try {
      const api = `${API.BASR_URL}trip/operation/${Number(
        user?.branch_id
      )}/${Number(user?.id)}?startDate=${dateFilter.startDate}&endDate=${
        dateFilter.endDate
      }`;
      const response = await fetch(api, {
        signal: abortControllerRef.current.signal,
      });
      if (!response.ok) throw new Error("Failed to fetch operation data");
      const data = await response.json();
      if (isMounted.current) {
        setOperationData(data);
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted");
      } else {
        console.error("Error fetching operation data:", error);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const fetchPickupData = async () => {
    setPickupLoading(true);
    try {
      const api = `${API.BASR_URL}PickupCollectionRequest/dashboard?startDate=${
        dateFilter?.startDate
      }&endDate=${dateFilter?.endDate}&branchId=${Number(user?.branch_id)}`;
      const response = await fetch(api);
      if (!response.ok) throw new Error("Failed to fetch pickup data");
      const data = await response.json();
      if (isMounted.current) {
        setPickupData(data);
      }
    } catch (error) {
      console.error("Error fetching pickup data:", error);
    } finally {
      if (isMounted.current) {
        setPickupLoading(false);
      }
    }
  };

  // New function to fetch pickup collection data
  const fetchPickupCollectionData = async () => {
    setPickupCollectionLoading(true);
    try {
      const api = `${API.BASR_URL}boxesNew/boxes_by_user_dashboard/${Number(
        user?.id
      )}?startDate=${dateFilter?.startDate}&endDate=${dateFilter?.endDate}`;
      const response = await fetch(api);
      if (!response.ok)
        throw new Error("Failed to fetch pickup collection data");
      const data = await response.json();
      if (isMounted.current) {
        setPickupCollectionData(data);
      }
    } catch (error) {
      console.error("Error fetching pickup collection data:", error);
    } finally {
      if (isMounted.current) {
        setPickupCollectionLoading(false);
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;
    fetchOperationData();
    fetchPickupData();
    fetchPickupCollectionData();
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line
  }, [dateFilter]);

  // Navigation handlers
  const handleTripSeeMore = () => {
    history.push("/auth/Trip");
  };

  const handlePickupSeeMore = () => {
    history.push("/auth/PickupCollectionRequest");
  };

  const handlePickupCollectionSeeMore = () => {
    history.push("/auth/Boxes");
  };

  // Columns for Inbound Trip Details Table
  const inboundTripColumns = [
    {
      title: "Trip ID",
      dataIndex: "id",
      key: "id",
      render: (text) => (
        <div className="cellWithIcon">
          <FaInbox className="icon" />
          <span className="cellText">{text}</span>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => (
        <span className="dateText">
          {moment(text).format("DD-MM-YYYY") || ""}
        </span>
      ),
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (_, record) => {
        return <span className="userText">{record?.user?.name || "N/A"}</span>;
      },
    },
    {
      title: "From Branch",
      dataIndex: "destination",
      key: "destination",
      render: (_, record) => {
        return (
          <span className="branchText">
            {record?.loggin_branch?.name || "N/A"}
          </span>
        );
      },
    },
    {
      title: "To Branch",
      dataIndex: "destination",
      key: "destination",
      render: (_, record) => (
        <span className="branchText">{record?.to_branch?.name || "N/A"}</span>
      ),
    },
    {
      title: "Vehicle No",
      dataIndex: "truck_no",
      key: "truck_no",
      render: (text) => <span className="vehicleText">{text || "N/A"}</span>,
    },
    {
      title: "Status",
      dataIndex: "trip_status",
      key: "trip_status",
      render: (status) => (
        <span className={`statusBadge ${getStatusStyle(status)}`}>
          {status?.replace("_", " ") || "N/A"}
        </span>
      ),
    },
  ];

  // Columns for Outbound Trip Details Table
  const outboundTripColumns = [
    {
      title: "Trip ID",
      dataIndex: "id",
      key: "id",
      render: (text) => (
        <div className="cellWithIcon">
          <FaPaperPlane className="icon" />
          <span className="cellText">{text}</span>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => (
        <span className="dateText">
          {moment(text).format("DD-MM-YYYY") || ""}
        </span>
      ),
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (_, record) => {
        return <span className="userText">{record?.user?.name || "N/A"}</span>;
      },
    },
    {
      title: "From Branch",
      dataIndex: "destination",
      key: "destination",
      render: (_, record) => {
        return (
          <span className="branchText">
            {record?.loggin_branch?.name || "N/A"}
          </span>
        );
      },
    },
    {
      title: "To Branch",
      dataIndex: "destination",
      key: "destination",
      render: (_, record) => (
        <span className="branchText">{record?.to_branch?.name || "N/A"}</span>
      ),
    },
    {
      title: "Vehicle No",
      dataIndex: "truck_no",
      key: "truck_no",
      render: (text) => <span className="vehicleText">{text || "N/A"}</span>,
    },
    {
      title: "Status",
      dataIndex: "trip_status",
      key: "trip_status",
      render: (status) => (
        <span className={`statusBadge ${getStatusStyle(status)}`}>
          {status?.replace("_", " ") || "N/A"}
        </span>
      ),
    },
  ];

  // Columns for Pending Trips Table
  const pendingTripColumns = [
    {
      title: "Trip ID",
      dataIndex: "id",
      key: "id",
      render: (text) => (
        <div className="cellWithIcon">
          <MdPending className="icon" />
          <span className="cellText">{text}</span>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => (
        <span className="dateText">
          {moment(text).format("DD-MM-YYYY") || ""}
        </span>
      ),
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (_, record) => {
        return <span className="userText">{record?.user?.name || "N/A"}</span>;
      },
    },
    {
      title: "From Branch",
      dataIndex: "frombranch",
      key: "frombranch",
      render: (_, record) => {
        return (
          <span className="branchText">
            {record?.loggin_branch?.name || "N/A"}
          </span>
        );
      },
    },
    {
      title: "To Branch",
      dataIndex: "tobranch",
      key: "tobranch",
      render: (_, record) => (
        <span className="branchText">{record?.to_branch?.name || "N/A"}</span>
      ),
    },
    {
      title: "Vehicle No",
      dataIndex: "truck_no",
      key: "truck_no",
      render: (text) => <span className="vehicleText">{text || "N/A"}</span>,
    },
    {
      title: "Status",
      dataIndex: "trip_status",
      key: "trip_status",
      render: (status) => (
        <span className={`statusBadge ${getStatusStyle(status || "pending")}`}>
          {status?.replace("_", " ") || "Pending"}
        </span>
      ),
    },
  ];

  // Columns for Assigned Pickup Table
  const assignedPickupColumns = [
    {
      title: "Pickup ID",
      dataIndex: "id",
      key: "id",
      render: (text) => (
        <div className="cellWithIcon">
          <FaUserCheck className="icon" />
          <span className="cellText">{text}</span>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => (
        <span className="dateText">
          {moment(text).format("DD-MM-YYYY") || ""}
        </span>
      ),
    },
    {
      title: "Sender Name",
      dataIndex: "sender_name",
      key: "sender_name",
      render: (text) => <span className="userText">{text || "N/A"}</span>,
    },
    {
      title: "Sender Phone",
      dataIndex: "sender_phone",
      key: "sender_phone",
      render: (text) => <span className="phoneText">{text || "N/A"}</span>,
    },
    {
      title: "Pickup Branch",
      dataIndex: "pickup_branchs",
      key: "pickup_branch",
      render: (_, record) => (
        <span className="branchText">
          {record?.pickup_branchs?.name || "N/A"}
        </span>
      ),
    },
    {
      title: "Assigned To",
      dataIndex: "assigned_user",
      key: "assigned_user",
      render: (_, record) => (
        <span className="userText">{record?.assigned_user?.name || "N/A"}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span className={`statusBadge ${getStatusStyle(status || "assigned")}`}>
          {status || "Assigned"}
        </span>
      ),
    },
  ];

  // Columns for Unassigned Pickup Table
  const unassignedPickupColumns = [
    {
      title: "Pickup ID",
      dataIndex: "id",
      key: "id",
      render: (text) => (
        <div className="cellWithIcon">
          <FaUserTimes className="icon" />
          <span className="cellText">{text}</span>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => (
        <span className="dateText">
          {moment(text).format("DD-MM-YYYY") || ""}
        </span>
      ),
    },
    {
      title: "Sender Name",
      dataIndex: "sender_name",
      key: "sender_name",
      render: (text) => <span className="userText">{text || "N/A"}</span>,
    },
    {
      title: "Sender Phone",
      dataIndex: "sender_phone",
      key: "sender_phone",
      render: (text) => <span className="phoneText">{text || "N/A"}</span>,
    },
    {
      title: "Pickup Branch",
      dataIndex: "pickup_branchs",
      key: "pickup_branch",
      render: (_, record) => (
        <span className="branchText">
          {record?.pickup_branchs?.name || "N/A"}
        </span>
      ),
    },
    {
      title: "Weight (kg)",
      dataIndex: "approximate_weight",
      key: "approximate_weight",
      render: (text) => <span className="weightText">{text || "N/A"}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span
          className={`statusBadge ${getStatusStyle(status || "unassigned")}`}
        >
          {status || "Unassigned"}
        </span>
      ),
    },
  ];

  // Columns for Assigned Pickup Collection Table
  const assignedPickupCollectionColumns = [
    {
      title: "Box Number",
      dataIndex: "box_number",
      key: "box_number",
      render: (text) => (
        <div className="cellWithIcon">
          <FaUserCheck className="icon" />
          <span className="cellText">{text}</span>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => (
        <span className="dateText">
          {moment(text).format("DD-MM-YYYY") || ""}
        </span>
      ),
    },
    {
      title: "Sender Name",
      dataIndex: "adhar_name",
      key: "adhar_name",
      render: (text) => <span className="userText">{text || "N/A"}</span>,
    },
    {
      title: "Sender Phone",
      dataIndex: "sender_mobile",
      key: "sender_mobile",
      render: (text) => <span className="phoneText">{text || "N/A"}</span>,
    },
    {
      title: "Weight (kg)",
      dataIndex: "weight",
      key: "weight",
      render: (text) => <span className="weightText">{text || "N/A"}</span>,
    },
    {
      title: "Total Charge",
      dataIndex: "total_charge",
      key: "total_charge",
      render: (text) => <span className="chargeText">₹{text || "N/A"}</span>,
    },
    {
      title: "Status",
      dataIndex: "status_message",
      key: "status_message",
      render: (status) => (
        <span className={`statusBadge ${getStatusStyle(status || "assigned")}`}>
          {status || "Assigned"}
        </span>
      ),
    },
  ];

  // Columns for Unassigned Pickup Collection Table
  const unassignedPickupCollectionColumns = [
    {
      title: "Box Number",
      dataIndex: "box_number",
      key: "box_number",
      render: (text) => (
        <div className="cellWithIcon">
          <FaUserTimes className="icon" />
          <span className="cellText">{text}</span>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => (
        <span className="dateText">
          {moment(text).format("DD-MM-YYYY") || ""}
        </span>
      ),
    },
    {
      title: "Sender Name",
      dataIndex: "adhar_name",
      key: "adhar_name",
      render: (text) => <span className="userText">{text || "N/A"}</span>,
    },
    {
      title: "Sender Phone",
      dataIndex: "sender_mobile",
      key: "sender_mobile",
      render: (text) => <span className="phoneText">{text || "N/A"}</span>,
    },
    {
      title: "Weight (kg)",
      dataIndex: "weight",
      key: "weight",
      render: (text) => <span className="weightText">{text || "N/A"}</span>,
    },
    {
      title: "Total Charge",
      dataIndex: "total_charge",
      key: "total_charge",
      render: (text) => <span className="chargeText">₹{text || "N/A"}</span>,
    },
    {
      title: "Status",
      dataIndex: "status_message",
      key: "status_message",
      render: (status) => (
        <span
          className={`statusBadge ${getStatusStyle(status || "unassigned")}`}
        >
          {status || "Unassigned"}
        </span>
      ),
    },
  ];

  // Tab configurations
  const tripTabs = [
    { key: "inbound", label: "Inbound" },
    { key: "outbound", label: "Outbound" },
    { key: "pending", label: "Pending" },
  ];

  const pickupTabs = [
    { key: "assigned", label: "Assigned" },
    { key: "unassigned", label: "Unassigned" },
  ];

  const pickupCollectionTabs = [
    { key: "assigned", label: "Assigned" },
    { key: "unassigned", label: "Unassigned" },
  ];

  // Function to get current trip table data based on selected tab
  const getCurrentTripTableData = () => {
    switch (tripTypeTab) {
      case "inbound":
        return operationData.inboundTripDetails.slice(0, 6);
      case "outbound":
        return operationData.outboundTripDetails.slice(0, 6);
      case "pending":
        return operationData.pendingTripDetails.slice(0, 6);
      default:
        return [];
    }
  };

  // Function to get current trip table columns based on selected tab
  const getCurrentTripTableColumns = () => {
    switch (tripTypeTab) {
      case "inbound":
        return inboundTripColumns;
      case "outbound":
        return outboundTripColumns;
      case "pending":
        return pendingTripColumns;
      default:
        return [];
    }
  };

  // Function to get current pickup table data based on selected tab
  const getCurrentPickupTableData = () => {
    switch (pickupTypeTab) {
      case "assigned":
        return pickupData.assignedPickupDetails.slice(0, 6);
      case "unassigned":
        return pickupData.unassignedPickupDetails.slice(0, 6);
      default:
        return [];
    }
  };

  // Function to get current pickup table columns based on selected tab
  const getCurrentPickupTableColumns = () => {
    switch (pickupTypeTab) {
      case "assigned":
        return assignedPickupColumns;
      case "unassigned":
        return unassignedPickupColumns;
      default:
        return [];
    }
  };

  // Function to get current pickup collection table data based on selected tab
  const getCurrentPickupCollectionTableData = () => {
    switch (pickupCollectionTypeTab) {
      case "assigned":
        return pickupCollectionData.totalAssignedPickupDatas.slice(0, 6);
      case "unassigned":
        return pickupCollectionData.totalUnassignedPickupDatas.slice(0, 6);
      default:
        return [];
    }
  };

  // Function to get current pickup collection table columns based on selected tab
  const getCurrentPickupCollectionTableColumns = () => {
    switch (pickupCollectionTypeTab) {
      case "assigned":
        return assignedPickupCollectionColumns;
      case "unassigned":
        return unassignedPickupCollectionColumns;
      default:
        return [];
    }
  };

  return (
    <div className="dashboardContainer">
      <div className="dashboardHeader">
        <h1 className="dashboardTitle">Operations Dashboard</h1>
        <DateFilter
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          fetchOperationData={fetchOperationData}
          loading={loading}
        />
      </div>

      {/* Trip Statistics */}
      <div className="statsSection">
        <h2 className="statsTitle">Trip Overview</h2>
        <div className="statsGrid">
          <StatCard
            title="Inbound Trips"
            value={operationData.inboundTripCount}
            icon={FaInbox}
            color="green"
            bgColor="bgGreen"
            onClick={() => history.push("/auth/Trip")}
          />
          <StatCard
            title="Outbound Trips"
            value={operationData.outboundTripCount}
            icon={FaPaperPlane}
            color="blue"
            bgColor="bgBlue"
            onClick={() => history.push("/auth/Trip")}
          />
          <StatCard
            title="Pending Trips"
            value={operationData.pendingTripCount}
            icon={MdPending}
            color="orange"
            bgColor="bgOrange"
            onClick={() => history.push("/auth/Trip")}
          />
        </div>
      </div>

      {/* Pickup Statistics */}
      <div className="statsSection">
        <h2 className="statsTitle">Pickup Request Overview</h2>
        <div className="statsGrid">
          <StatCard
            title="Total Pickups"
            value={pickupData.totalPickupCount}
            icon={FaBoxes}
            color="purple"
            bgColor="bgPurple"
            onClick={() => history.push("/auth/pickups")}
          />
          <StatCard
            title="Assigned Pickups"
            value={pickupData.assignedPickupCount}
            icon={FaUserCheck}
            color="green"
            bgColor="bgGreen"
            onClick={() => history.push("/auth/pickups")}
          />
          <StatCard
            title="Unassigned Pickups"
            value={pickupData.unassignedPickupCount}
            icon={FaUserTimes}
            color="red"
            bgColor="bgRed"
            onClick={() => history.push("/auth/pickups")}
          />
        </div>
      </div>

      {/* Pickup Collection Statistics */}
      <div className="statsSection">
        <h2 className="statsTitle">Pickup Collection Overview</h2>
        <div className="statsGrid">
          <StatCard
            title="Total Boxes"
            value={pickupCollectionData.totalPickupCount}
            icon={FaBoxes}
            color="indigo"
            bgColor="bgIndigo"
            onClick={() => history.push("/auth/Boxes")}
          />
          <StatCard
            title="Assigned Boxes"
            value={pickupCollectionData.totalAssignedPickupCount}
            icon={FaUserCheck}
            color="green"
            bgColor="bgGreen"
            onClick={() => history.push("/auth/Boxes")}
          />
          <StatCard
            title="Unassigned Boxes"
            value={pickupCollectionData.totalUnassignedPickupCount}
            icon={FaUserTimes}
            color="red"
            bgColor="bgRed"
            onClick={() => history.push("/auth/Boxes")}
          />
        </div>
      </div>

      {/* Trip Details Section */}
      <div className="tableSection">
        <SectionHeader title="Trip Details" onSeeMore={handleTripSeeMore} />

        {loading ? (
          <div className="loadingContainer">
            <div className="spinner"></div>
            <span>Loading trip data...</span>
          </div>
        ) : (
          <>
            <TabNavigation
              tabs={tripTabs}
              activeTab={tripTypeTab}
              onTabChange={setTripTypeTab}
              color="#3b82f6"
            />

            <div className="tableContainer">
              <Table
                columns={getCurrentTripTableColumns()}
                dataSource={getCurrentTripTableData()}
                rowKey={(record, index) => `trip-${index}`}
                pagination={false}
                rowClassName="tableRow"
                className="dashboardTable"
              />
            </div>
          </>
        )}
      </div>

      {/* Pickup Details Section */}
      <div className="tableSection">
        <SectionHeader
          title="Pickup Request Details"
          onSeeMore={handlePickupSeeMore}
        />

        {pickupLoading ? (
          <div className="loadingContainer">
            <div className="spinner"></div>
            <span>Loading pickup data...</span>
          </div>
        ) : (
          <>
            <TabNavigation
              tabs={pickupTabs}
              activeTab={pickupTypeTab}
              onTabChange={setPickupTypeTab}
              color="#10b981"
            />

            <div className="tableContainer">
              <Table
                columns={getCurrentPickupTableColumns()}
                dataSource={getCurrentPickupTableData()}
                rowKey={(record, index) => `pickup-${index}`}
                pagination={false}
                rowClassName="tableRow"
                className="dashboardTable"
              />
            </div>
          </>
        )}
      </div>

      {/* Pickup Collection Details Section */}
      <div className="tableSection">
        <SectionHeader
          title="Pickup Collection Details"
          onSeeMore={handlePickupCollectionSeeMore}
        />

        {pickupCollectionLoading ? (
          <div className="loadingContainer">
            <div className="spinner"></div>
            <span>Loading pickup collection data...</span>
          </div>
        ) : (
          <>
            <TabNavigation
              tabs={pickupCollectionTabs}
              activeTab={pickupCollectionTypeTab}
              onTabChange={setPickupCollectionTypeTab}
              color="#8b5cf6"
            />

            <div className="tableContainer">
              <Table
                columns={getCurrentPickupCollectionTableColumns()}
                dataSource={getCurrentPickupCollectionTableData()}
                rowKey={(record, index) => `pickup-collection-${index}`}
                pagination={false}
                rowClassName="tableRow"
                className="dashboardTable"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OperationsDashboard;
